/**
 * Centralized logging utility
 * Replaces console.log/error/warn with environment-aware logging
 * Integrates with error tracking service
 */

import { errorTracking } from '../lib/errorTracking';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

class Logger {
  private shouldLog(level: LogLevel): boolean {
    // Always log errors and warnings
    if (level === 'error' || level === 'warn') {
      return true;
    }

    // Only log debug/info if debug logs are enabled
    if (import.meta.env.VITE_ENABLE_DEBUG_LOGS === 'true') {
      return true;
    }

    return false;
  }

  debug(...args: unknown[]): void {
    if (this.shouldLog('debug')) {
      console.debug('[DEBUG]', ...args);
    }
  }

  info(...args: unknown[]): void {
    if (this.shouldLog('info')) {
      console.info('[INFO]', ...args);
    }
  }

  warn(...args: unknown[]): void {
    console.warn('[WARN]', ...args);
    
    // Track warnings in error tracking
    const message = args.map(arg => 
      typeof arg === 'string' ? arg : JSON.stringify(arg)
    ).join(' ');
    errorTracking.captureMessage(message, 'warning');
  }

  error(...args: unknown[]): void {
    console.error('[ERROR]', ...args);
    
    // Track errors in error tracking service
    const errorMessage = args.map(arg => 
      typeof arg === 'string' ? arg : JSON.stringify(arg)
    ).join(' ');
    
    // If first arg is an Error object, capture it directly
    if (args[0] instanceof Error) {
      errorTracking.captureException(args[0], {
        additionalContext: args.slice(1),
      });
    } else {
      // Otherwise, create an error from the message
      errorTracking.captureException(
        new Error(errorMessage),
        {
          logArgs: args,
        }
      );
    }
  }

  /**
   * Log with context (useful for debugging)
   */
  logWithContext(level: LogLevel, message: string, context?: Record<string, unknown>): void {
    if (this.shouldLog(level)) {
      const logMethod = this[level].bind(this);
      if (context) {
        logMethod(message, context);
      } else {
        logMethod(message);
      }
    }

    // Add breadcrumb for error tracking
    if (level === 'error' || level === 'warn') {
      const breadcrumbLevel: 'info' | 'warning' | 'error' = level === 'warn' ? 'warning' : level;
      errorTracking.addBreadcrumb(
        message,
        'logger',
        breadcrumbLevel,
        context
      );
    }
  }
}

export const logger = new Logger();

